import { defineStore } from 'pinia'

interface ScrollOptions {
  selector: string
  offset?: number
  timeout?: number
}

interface ForceLoadPayload {
  value: boolean
}

interface LazyState {
  load: boolean
  pendingScrolls: number
}

const WAIT_FOR_ELEMENT_TIMEOUT = 5000
const SETTLE_INTERVAL = 100
const SETTLE_TIMEOUT = 3000
const STABLE_SAMPLES = 2
const SETTLED_SAMPLES = 5
const SCROLL_END_TIMEOUT = 5000
const MAX_CORRECTIONS = 4
const POSITION_TOLERANCE = 4
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown']
// Milliseconds per pixel, same pace as the smooth scroll used everywhere else
const SCROLL_SPEED = 0.5
const SCROLL_DURATION_MIN = 300
const SCROLL_DURATION_MAX = 1500

export const useLazyStore = defineStore('lazyForceLoad', {
  state: (): LazyState => ({
    load: false,
    pendingScrolls: 0
  }),

  getters: {
    forceLoad: (state): boolean => state.load
  },

  actions: {
    async scroll({
      selector,
      offset,
      timeout = WAIT_FOR_ELEMENT_TIMEOUT
    }: ScrollOptions) {
      this.pendingScrolls++
      this.setForceLoad({ value: true })

      try {
        const elem = await _waitForElement(selector, timeout)

        if (elem) {
          await _waitForStableLayout(elem)
          await _scrollTo(elem, offset)
        }
      } finally {
        this.pendingScrolls--
      }
    },

    setForceLoad({ value }: ForceLoadPayload) {
      // A pending scroll needs every LazyComponent rendered until it found its
      // target, so it wins over the reset the router does on every navigation.
      if (!value && this.pendingScrolls > 0) return

      this.load = value
    }
  }
})

/**
 * Resolves as soon as the selector matches, since lazily rendered content can
 * enter the DOM at any point after forceLoad was set.
 */
const _waitForElement = (
  selector: string,
  timeout: number
): Promise<Element | null> => {
  const existing = document.querySelector(selector)
  if (existing) return Promise.resolve(existing)

  return new Promise((resolve) => {
    let timer: ReturnType<typeof setTimeout>

    const finish = (elem: Element | null) => {
      clearTimeout(timer)
      observer.disconnect()
      resolve(elem)
    }

    const observer = new MutationObserver(() => {
      const elem = document.querySelector(selector)
      if (elem) finish(elem)
    })

    timer = setTimeout(() => finish(null), timeout)
    observer.observe(document.body, { childList: true, subtree: true })
  })
}

/**
 * The content above the target keeps growing while it renders (lazy blocks,
 * images without reserved space), so scrolling right away lands too high up.
 * Waits until the target stopped moving within the document.
 */
const _waitForStableLayout = async (
  elem: Element,
  samples = STABLE_SAMPLES,
  cancelled: () => boolean = () => false
) => {
  let stable = 0
  let previousTop = _documentTop(elem)

  for (let waited = 0; waited < SETTLE_TIMEOUT; waited += SETTLE_INTERVAL) {
    await _delay(SETTLE_INTERVAL)
    if (cancelled()) return

    const top = _documentTop(elem)
    stable = top === previousTop ? stable + 1 : 0
    previousTop = top

    if (stable >= samples) return
  }
}

/**
 * Scrolls to the target and repeats until we really arrived there, since the
 * page can still grow above it after the animation finished.
 */
const _scrollTo = async (elem: Element, offset = 0) => {
  const userTookOver = _watchUserScroll()

  try {
    for (let correction = 0; correction <= MAX_CORRECTIONS; correction++) {
      await _animateTo(elem, offset, () => userTookOver.happened)
      await _waitForScrollEnd()
      if (userTookOver.happened) return

      // Arriving is not the same as staying: a lull between two bursts of lazy
      // content already counts as a stable layout, and everything that renders
      // above the target afterwards pushes it out of view again. So the
      // position only counts once the target really came to rest.
      await _waitForStableLayout(
        elem,
        SETTLED_SAMPLES,
        () => userTookOver.happened
      )

      if (userTookOver.happened || _isScrolledTo(elem, offset)) return
    }
  } finally {
    userTookOver.stop()
  }
}

/**
 * Follows the target while animating instead of aiming at the position it had
 * when the animation started, so late rendered content above it shows up as a
 * longer scroll and not as a second one at a different speed.
 */
const _animateTo = (
  elem: Element,
  offset: number,
  cancelled: () => boolean
): Promise<void> => {
  const from = window.scrollY
  const duration = _duration(Math.abs(_target(elem, offset) - from))

  if (duration === 0) {
    window.scrollTo(0, _target(elem, offset))
    return Promise.resolve()
  }

  return new Promise((resolve) => {
    let start: number | null = null

    const step = (now: number) => {
      start ??= now
      if (cancelled()) return resolve()

      const progress = Math.min((now - start) / duration, 1)
      const target = _target(elem, offset)
      window.scrollTo(0, Math.round(from + (target - from) * _ease(progress)))

      if (progress < 1) {
        requestAnimationFrame(step)
        return
      }
      resolve()
    }

    requestAnimationFrame(step)
  })
}

const _duration = (distance: number) => {
  if (distance <= POSITION_TOLERANCE || _prefersReducedMotion()) return 0

  return Math.min(
    Math.max(distance * SCROLL_SPEED, SCROLL_DURATION_MIN),
    SCROLL_DURATION_MAX
  )
}

const _prefersReducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false

const _ease = (progress: number) =>
  progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2

const _target = (elem: Element, offset: number) =>
  Math.max(_documentTop(elem) - offset, 0)

const _isScrolledTo = (elem: Element, offset: number) => {
  const target = _target(elem, offset)
  if (Math.abs(window.scrollY - target) <= POSITION_TOLERANCE) return true

  // The target sits at the end of the page and cannot come any closer
  const maxScroll = document.documentElement.scrollHeight - window.innerHeight
  return window.scrollY >= maxScroll - POSITION_TOLERANCE
}

const _waitForScrollEnd = async () => {
  let stable = 0
  let previousY = window.scrollY

  for (let waited = 0; waited < SCROLL_END_TIMEOUT; waited += SETTLE_INTERVAL) {
    await _delay(SETTLE_INTERVAL)

    stable = window.scrollY === previousY ? stable + 1 : 0
    previousY = window.scrollY

    if (stable >= STABLE_SAMPLES) return
  }
}

/**
 * Correcting the scroll position must never fight someone who scrolls away.
 */
const _watchUserScroll = () => {
  const state = {
    happened: false,
    stop: () => {
      USER_SCROLL_EVENTS.forEach((event) =>
        window.removeEventListener(event, onUserScroll)
      )
    }
  }

  const onUserScroll = () => {
    state.happened = true
  }

  USER_SCROLL_EVENTS.forEach((event) =>
    window.addEventListener(event, onUserScroll, { passive: true })
  )

  return state
}

const _documentTop = (elem: Element) =>
  Math.round(elem.getBoundingClientRect().top + window.scrollY)

const _delay = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
